import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "audio-attachment" }
const _hoisted_2 = { class: "audio-attachment__card" }
const _hoisted_3 = { class: "audio-attachment__icon-wrapper" }
const _hoisted_4 = { class: "audio-attachment__content" }
const _hoisted_5 = { class: "audio-attachment__info" }
const _hoisted_6 = { class: "audio-attachment__name" }
const _hoisted_7 = {
  key: 0,
  class: "audio-attachment__duration"
}
const _hoisted_8 = {
  key: 1,
  class: "audio-attachment__size"
}
const _hoisted_9 = ["disabled"]
const _hoisted_10 = ["src"]

import { ref, computed, watch } from "vue";
import type { Attachment } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import { useAttachmentLoader } from "@/composables/attachments/useAttachmentLoader";
import { useAttachmentDownload } from "@/composables/attachments/useAttachmentDownload";
import ProgressCircle from "./ProgressCircle.vue";

interface Props {
  attachment: Attachment;
  messageId?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AudioAttachment',
  props: {
    attachment: {},
    messageId: {}
  },
  setup(__props: any) {

const props = __props;

const audioRef = ref<HTMLAudioElement | null>(null);
const isPlaying = ref(false);
const audioLoaded = ref(false);

const { isLoading, loadingProgress, blobUrl, loadAttachment, getAttachmentUrl } =
  useAttachmentLoader(props.attachment, props.messageId, () => {});

const {
  isDownloading,
  loadingProgress: downloadProgress,
  download,
} = useAttachmentDownload(props.attachment, props.messageId);

const resolvedAudioUrl = computed(() => {
  if (blobUrl.value) return blobUrl.value;
  return getAttachmentUrl(props.attachment.url);
});

const loadAudio = async () => {
  const blob = await loadAttachment("audio/mpeg");
  if (blob) {
    audioLoaded.value = true;
  }
};

const handleLoadStart = async () => {
  if (
    props.attachment.url?.startsWith("att://") &&
    !blobUrl.value &&
    props.messageId &&
    props.attachment.id
  ) {
    const blob = await loadAttachment("audio/mpeg");
    if (blob && audioRef.value) {
      audioRef.value.src = blobUrl.value!;
    }
  }
};

const togglePlay = async () => {
  if (!audioRef.value) return;

  if (
    props.attachment.url?.startsWith("att://") &&
    !blobUrl.value &&
    props.messageId &&
    props.attachment.id
  ) {
    const blob = await loadAttachment("audio/mpeg");
    if (blob && audioRef.value) {
      audioRef.value.src = blobUrl.value!;
      audioLoaded.value = true;
    }
  }

  if (audioRef.value.paused) {
    await audioRef.value.play();
  } else {
    audioRef.value.pause();
  }
};

const handleDownload = () => {
  download("audio/mpeg", `audio_${props.attachment.id}`);
};

watch(
  () => resolvedAudioUrl.value,
  (url) => {
    if (url && !props.attachment.url?.startsWith("att://")) {
      audioLoaded.value = true;
    }
  },
  { immediate: true }
);

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(Icon), {
          icon: "mdi:music-note",
          class: "audio-attachment__icon"
        })
      ]),
      (audioLoaded.value && resolvedAudioUrl.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "audio-attachment__play",
            onClick: _withModifiers(togglePlay, ["stop"]),
            title: "Play/Pause"
          }, [
            (isPlaying.value)
              ? (_openBlock(), _createBlock(_unref(Icon), {
                  key: 0,
                  icon: "mdi:pause",
                  class: "audio-attachment__play-icon"
                }))
              : (_openBlock(), _createBlock(_unref(Icon), {
                  key: 1,
                  icon: "mdi:play",
                  class: "audio-attachment__play-icon"
                }))
          ]))
        : (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: "audio-attachment__preview-btn",
            onClick: loadAudio
          }, [
            _createVNode(_unref(Icon), { icon: "majesticons:arrow-down" })
          ])),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("span", _hoisted_6, _toDisplayString(__props.attachment.name || "Audio"), 1),
          (__props.attachment.duration)
            ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(formatDuration(__props.attachment.duration)), 1))
            : _createCommentVNode("", true),
          (
              __props.attachment.file_size ||
              (typeof __props.attachment.size === 'number' ? __props.attachment.size : undefined)
            )
            ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(formatFileSize(
                __props.attachment.file_size ||
                  (typeof __props.attachment.size === "number" ? __props.attachment.size : undefined)
              )), 1))
            : _createCommentVNode("", true)
        ])
      ]),
      (audioLoaded.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 2,
            class: "audio-attachment__download",
            onClick: _withModifiers(handleDownload, ["stop"]),
            disabled: _unref(isDownloading),
            title: "Download audio"
          }, [
            (_unref(isDownloading) && _unref(downloadProgress) > 0)
              ? (_openBlock(), _createBlock(ProgressCircle, {
                  key: 0,
                  progress: _unref(downloadProgress),
                  size: "small",
                  "bg-stroke": "rgba(0, 0, 0, 0.1)",
                  "fill-stroke": "#111827",
                  "show-text": false
                }, null, 8, ["progress"]))
              : (_unref(isDownloading))
                ? (_openBlock(), _createBlock(_unref(Icon), {
                    key: 1,
                    icon: "mdi:loading",
                    class: "audio-attachment__spinner"
                  }))
                : (_openBlock(), _createBlock(_unref(Icon), {
                    key: 2,
                    icon: "mdi:download"
                  }))
          ], 8, _hoisted_9))
        : _createCommentVNode("", true),
      (audioLoaded.value && resolvedAudioUrl.value)
        ? (_openBlock(), _createElementBlock("audio", {
            key: 3,
            ref_key: "audioRef",
            ref: audioRef,
            src: resolvedAudioUrl.value,
            class: "audio-attachment__player",
            onLoadstart: handleLoadStart,
            onPlay: _cache[0] || (_cache[0] = ($event: any) => (isPlaying.value = true)),
            onPause: _cache[1] || (_cache[1] = ($event: any) => (isPlaying.value = false)),
            onEnded: _cache[2] || (_cache[2] = ($event: any) => (isPlaying.value = false))
          }, null, 40, _hoisted_10))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})